import React from 'react';
import styled, { css } from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import { scaledPx } from 'containers/Intelligence/theme/scale';

/**
 * `band` is the Intelligence section band. `compact` is the Overwatch section bar (PLA-9674,
 * 5726:7928): a 53px r12 strip carrying the same section title and description type as the band.
 */
export type SectionHeaderVariant = 'band' | 'compact';

export interface ISectionHeaderProps {
  /** Fixed section band copy, or the industry-driven title of an industry section. */
  title: string;
  description?: string;
  /**
   * @deprecated Has no effect - every section band is drawn banded. Accepted only so the three
   * sections still passing it keep compiling; drop it from them and from here together.
   */
  banded?: boolean;
  /**
   * Element the band renders as. A band drawn inside a card's own `header` — 589-29260 puts the
   * section title inside the card frame — must not nest one `header` in another, which the HTML
   * content model forbids. Not named `as`: that is styled-components' own prop, and a caller
   * wrapping this in `styled()` would have it swap the component out instead of reaching the band.
   */
  element?: 'header' | 'div';
  /** Defaults to `band`, the strip every Intelligence section draws. */
  variant?: SectionHeaderVariant;
  /**
   * Controls drawn at the strip's right end - filters, a call to action, a collapse toggle, the
   * info mark. Without them the band renders exactly as it always has.
   */
  actions?: React.ReactNode;
  /** The description's ink, where a screen draws it in a colour of its own. */
  descriptionColor?: string;
  className?: string;
}

// 589:32394 is a 1360x77 strip, but the 77 is what these insets plus the type add up to, so the
// height is left to fall out and the strip tracks a fluid container instead of pinning 1360px.
const BAND_PADDING = '10px 23px 15px';
const BAND_TITLE_GAP = '3px';
// Band bottom 77 -> first card top 94 in the Customer Base export. Off the 4px space ramp, so it is
// a literal here rather than a token; it moves with the 50px section->section gap in
// CustomerInsightsPage - applying either alone worsens the rhythm.
const BAND_CONTENT_GAP = '17px';

// Canonical section-heading style for the Intelligence Suite: a new section gets its heading right
// by rendering <SectionHeader title description /> and declaring no type, colour or spacing of its
// own. Every band is styled identically here, including the three the screens inset differently.
const COMPACT_TITLE_GAP = scaledPx(5);

const compactStrip = css`
  min-height: ${scaledPx(53)};
  padding: ${scaledPx(8)} ${scaledPx(10)} ${scaledPx(8)} ${scaledPx(16)};
  border-radius: ${tokens.radius.md};
  margin-bottom: 0;
`;

/* The strip turns into a row only when it carries actions, so a band without them keeps its
   column layout and its markup. */
const actionRow = css`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  && > * + * {
    margin-top: 0;
    margin-left: ${tokens.space['16']};
  }
`;

const SectionHeaderWrapper = styled.header<{ $compact: boolean; $actions: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${tokens.color['surface-section']};
  border-radius: ${tokens.radius.lg};
  padding: ${BAND_PADDING};
  /* Each section stacks its children with a sibling margin rule that loses the cascade to
     CardGrid's own margin reset, so the band carries the gap down to its cards itself. */
  margin-bottom: ${BAND_CONTENT_GAP};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). The doubled class
     outranks the children's own margin reset, which is injected later and would otherwise win the
     cascade on equal specificity and collapse this gap to nothing. */
  && > * + * {
    margin-top: ${BAND_TITLE_GAP};
  }

  ${({ $compact }) => ($compact ? compactStrip : '')}
  ${({ $actions }) => ($actions ? actionRow : '')}
`;

const Heading = styled.div<{ $compact: boolean }>`
  min-width: 0;

  /* Doubled for the same reason as the band's own rule: the description's margin reset is injected
     later and would otherwise win on equal specificity. */
  && > * + * {
    margin-top: ${({ $compact }) => ($compact ? COMPACT_TITLE_GAP : BAND_TITLE_GAP)};
  }
`;

const Actions = styled.div`
  display: flex;
  flex: none;
  align-items: center;
`;

// The page title is a PageHeader concern, so a section band sits one level below it.
const Title = styled.h2<{ $compact: boolean }>`
  margin: 0;
  font-size: ${tokens.type['section-title-size']};
  font-weight: ${tokens.type['section-title-weight']};
  line-height: ${tokens.type['section-title-line']};

  /* IntelligenceContentScope colours every h1-h6 through a descendant selector, which outranks a
     single class; the doubled class is what lets the band title keep its own near-white. */
  && {
    color: ${({ $compact }) =>
      $compact ? tokens.color['text-primary'] : tokens.color['text-title']};
  }
`;

const Description = styled.p<{ $compact: boolean; $color?: string }>`
  margin: 0;
  color: ${({ $compact, $color }) =>
    $color || ($compact ? tokens.color['text-secondary'] : tokens.color['text-band-description'])};
  font-size: ${tokens.type['band-description-size']};
  font-weight: ${tokens.type['band-description-weight']};
  line-height: ${tokens.type['band-description-line']};
`;

const SectionHeader: React.FC<ISectionHeaderProps> = ({
  title,
  description,
  element = 'header',
  variant = 'band',
  actions,
  descriptionColor,
  className
}) => {
  const compact = variant === 'compact';
  const hasActions = actions !== undefined && actions !== null;
  const heading = (
    <>
      <Title $compact={compact} data-testid="section-header-title">
        {title}
      </Title>
      {description ? (
        <Description
          $compact={compact}
          $color={descriptionColor}
          data-testid="section-header-description"
        >
          {description}
        </Description>
      ) : null}
    </>
  );

  return (
    <SectionHeaderWrapper
      as={element}
      $compact={compact}
      $actions={hasActions}
      className={className}
      data-testid="section-header"
      data-banded="true"
    >
      {hasActions ? (
        <>
          <Heading $compact={compact}>{heading}</Heading>
          <Actions data-testid="section-header-actions">{actions}</Actions>
        </>
      ) : (
        heading
      )}
    </SectionHeaderWrapper>
  );
};

export default SectionHeader;
